import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Ticket, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

function sessionId() {
  let s = localStorage.getItem("dt-chat-session");
  if (!s) {
    s = `s-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    localStorage.setItem("dt-chat-session", s);
  }
  return s;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("chat"); // chat | ticket
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hi! I'm Destiny Bot, the Destiny Tip assistant. Ask me about features, pricing, applications, tipping or your account." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [topics, setTopics] = useState([]);
  const [ticket, setTicket] = useState({ subject: "", message: "", name: "", email: "" });
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/chat/topics").then((r) => setTopics(r.data.topics || [])).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, mode, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { from: "user", text: msg }]);
    setBusy(true);
    try {
      const r = await api.post("/chat", { session_id: sessionId(), message: msg });
      setMessages((m) => [...m, { from: "bot", text: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { from: "bot", text: apiError(e) }]);
    } finally { setBusy(false); }
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/chat/ticket", ticket);
      toast.success(r.data.message);
      setMessages((m) => [...m, { from: "bot", text: `Ticket created: “${ticket.subject}”. Our team will reply to you shortly.` }]);
      setTicket({ subject: "", message: "", name: "", email: "" });
      setMode("chat");
    } catch (e2) {
      toast.error(apiError(e2));
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-dt-bg border border-dt-border rounded-xl px-3.5 py-2.5 text-sm text-dt-text placeholder:text-dt-muted focus:outline-none focus:border-dt-accent";

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close help chat" : "Open help chat"}
        className="fixed bottom-5 right-5 z-[86] w-13 h-13 w-[52px] h-[52px] rounded-full bg-dt-card border border-dt-accent/40 text-dt-accent flex items-center justify-center shadow-[0_0_20px_rgba(141,185,232,0.25)] transition-all duration-200 hover:scale-110 hover:shadow-[0_0_30px_rgba(141,185,232,0.5)]"
        data-testid="chat-toggle"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>

      {open && (
        <div className="fixed bottom-[84px] right-5 z-[86] w-[92vw] max-w-sm card-dt !bg-dt-bg2/97 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
          style={{ height: "min(560px, 70vh)" }} data-testid="chat-panel" role="dialog" aria-label="Destiny Tip help chat">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-dt-border bg-dt-card">
            <img src="/assets/logo.png" alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-dt-border" />
            <div className="flex-1">
              <p className="font-heading font-semibold text-sm text-dt-text">Destiny Bot — Help Assistant</p>
              <p className="text-[10px] text-[#4ade80] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] live-dot" /> Online</p>
            </div>
            <button onClick={() => setMode(mode === "ticket" ? "chat" : "ticket")}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-dt-accent border border-dt-accent/30 rounded-full px-3 py-1.5 hover:bg-dt-accent/10 transition-colors"
              data-testid="chat-ticket-toggle">
              <Ticket className="w-3.5 h-3.5" /> {mode === "ticket" ? "Back to chat" : "Create Support Ticket"}
            </button>
          </div>

          {mode === "chat" ? (
            <>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.from === "user" ? "bg-dt-accent/20 text-dt-text rounded-br-sm" : "bg-dt-card border border-dt-border text-dt-text rounded-bl-sm"}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="flex justify-start"><div className="bg-dt-card border border-dt-border rounded-2xl rounded-bl-sm px-4 py-3">
                    <Loader2 className="w-4 h-4 animate-spin text-dt-accent" />
                  </div></div>
                )}
                <div ref={bottomRef} />
              </div>
              {topics.length > 0 && messages.length <= 1 && (
                <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                  {topics.slice(0, 4).map((t) => (
                    <button key={t} onClick={() => send(t)}
                      className="text-[11px] text-dt-muted border border-dt-border rounded-full px-3 py-1.5 hover:border-dt-accent hover:text-dt-text transition-colors">
                      {t}
                    </button>
                  ))}
                </div>
              )}
              <div className="p-3 border-t border-dt-border flex gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about Destiny Tip…" className={`${inputCls} !rounded-full`} data-testid="chat-input" maxLength={800} />
                <button onClick={() => send()} disabled={busy || !input.trim()} aria-label="Send message"
                  className="w-10 h-10 rounded-full bg-dt-silver text-dt-bg flex items-center justify-center hover:bg-white transition-colors disabled:opacity-50 shrink-0"
                  data-testid="chat-send">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={submitTicket} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ticket-form">
              <p className="text-xs text-dt-muted">Can't find your answer? Create a support ticket — our team replies by email and in your dashboard.</p>
              <input required minLength={3} placeholder="Subject" className={inputCls} value={ticket.subject}
                onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} data-testid="ticket-subject" maxLength={140} />
              <textarea required minLength={3} rows={4} placeholder="Describe your issue…" className={inputCls} value={ticket.message}
                onChange={(e) => setTicket({ ...ticket, message: e.target.value })} data-testid="ticket-message" maxLength={4000} />
              <input placeholder="Your name (optional if logged in)" className={inputCls} value={ticket.name}
                onChange={(e) => setTicket({ ...ticket, name: e.target.value })} data-testid="ticket-name" />
              <input type="email" placeholder="Your email (optional if logged in)" className={inputCls} value={ticket.email}
                onChange={(e) => setTicket({ ...ticket, email: e.target.value })} data-testid="ticket-email" />
              <button type="submit" disabled={busy}
                className="w-full bg-dt-silver text-dt-bg font-semibold rounded-full px-4 py-2.5 text-sm hover:bg-white transition-colors disabled:opacity-50"
                data-testid="ticket-submit">
                {busy ? "Creating…" : "Create ticket"}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
